/**
 * Default composer type: free-form text with optional image/video attachments.
 *
 * This is the most general post type and the one the composer opens to by
 * default. If there are no attachments, the post is `type: 'text'` on the
 * backend; with attachments it's `type: 'media'` and each file is uploaded
 * to `/medias/profile` before the post row itself is created.
 *
 * The composer can also save attachments to the active identity's portfolio
 * (the user's, or the acting-as business's). When that toggle is on, the
 * upload writes to `type=portfolio` instead of `type=post_media`; the same
 * profile_medias row is then referenced by post_media for the post AND
 * surfaces in portfolio queries for the user/business.
 */

import {useEffect, useRef} from 'react'
import {uploadFile, uploadVideoResume} from 'api'
import useActingAs from 'hooks/useActingAs'
import closeIcon from 'assets/img/close.svg'

const MAX_BODY_LENGTH = 5000
const MAX_MEDIA_COUNT = 10
const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024 // 10 MB
const MAX_VIDEO_SIZE_BYTES = 100 * 1024 * 1024 // 100 MB

const inferMediaType = (file) => {
  if (file?.type?.startsWith('video/')) return 'video'
  if (file?.type?.startsWith('image/')) return 'image'
  return null
}

const createInitialState = () => ({
  body: '',
  // Attachments stay in state as { file, mediaType, previewUrl }. previewUrl
  // is an object URL that the Body component is responsible for revoking.
  attachments: [],
  // When true, attachments are uploaded as `type=portfolio` so they also
  // appear in the active identity's portfolio (user's or acting-as business's).
  saveToPortfolio: false,
  // When true, the first attached video is also uploaded as the user's
  // intro / video-resume after the post is created. UAT (2026/04/28):
  // Amit said the composer should ask "do you want to make this your
  // introduction video?" when a user uploads a video — currently the
  // intro-video flow lives behind a separate UI and adoption is low.
  setAsIntroVideo: false,
})

const PostBody = ({state, setState, setError}) => {
  const fileInputRef = useRef(null)
  const textareaRef = useRef(null)
  const {actorKind, actor} = useActingAs()

  useEffect(() => {
    const t = setTimeout(() => textareaRef.current?.focus(), 50)
    return () => clearTimeout(t)
  }, [])

  // Revoke attachment object URLs on unmount so the browser can release
  // memory. We rely on the shell unmounting the body when the user cancels
  // or the composer closes.
  useEffect(() => {
    return () => {
      state.attachments.forEach((a) => {
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFilesSelected = (files) => {
    const incoming = Array.from(files || [])
    if (incoming.length === 0) return

    const remainingSlots = MAX_MEDIA_COUNT - state.attachments.length
    if (remainingSlots <= 0) {
      setError(`You can attach up to ${MAX_MEDIA_COUNT} items per post.`)
      return
    }

    const next = []
    for (const file of incoming.slice(0, remainingSlots)) {
      const mediaType = inferMediaType(file)
      if (!mediaType) {
        setError('Only image and video files are supported.')
        continue
      }
      const cap =
        mediaType === 'video' ? MAX_VIDEO_SIZE_BYTES : MAX_IMAGE_SIZE_BYTES
      if (file.size > cap) {
        setError(
          mediaType === 'video'
            ? 'Videos must be under 100 MB.'
            : 'Images must be under 10 MB.'
        )
        continue
      }
      next.push({file, mediaType, previewUrl: URL.createObjectURL(file)})
    }
    if (next.length > 0) {
      setState((prev) => ({
        ...prev,
        attachments: [...prev.attachments, ...next],
      }))
      setError('')
    }
  }

  const handleRemoveAttachment = (index) => {
    setState((prev) => {
      const removed = prev.attachments[index]
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl)
      return {
        ...prev,
        attachments: prev.attachments.filter((_, i) => i !== index),
      }
    })
  }

  const gridClass =
    state.attachments.length === 1 ? 'grid-cols-1' : 'grid-cols-2'

  return (
    <div className="flex flex-col gap-3">
      <textarea
        ref={textareaRef}
        value={state.body}
        onChange={(e) => {
          setState((prev) => ({...prev, body: e.target.value}))
          setError('')
        }}
        placeholder="What do you want to talk about?"
        maxLength={MAX_BODY_LENGTH}
        rows={state.attachments.length > 0 ? 3 : 6}
        className="w-full resize-none border-0 outline-none text-base text-sharpenn-black placeholder:text-green-gray bg-transparent focus:ring-0"
      />

      {state.attachments.length > 0 && (
        <div className={`grid gap-2 ${gridClass}`}>
          {state.attachments.map((a, index) => (
            <div
              key={a.previewUrl}
              className="relative rounded-xl overflow-hidden bg-black aspect-square ring-1 ring-black/5"
            >
              {a.mediaType === 'image' ? (
                <img
                  src={a.previewUrl}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                <video
                  src={a.previewUrl}
                  className="w-full h-full object-cover"
                  muted
                  playsInline
                />
              )}
              <button
                type="button"
                onClick={() => handleRemoveAttachment(index)}
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center transition-colors"
                aria-label="Remove attachment"
              >
                <img src={closeIcon} alt="" className="w-3 h-3 invert" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* "Save to portfolio" toggle. Only meaningful when there are
          attachments — a text-only post has nothing to save. The label
          adapts to the acting-as identity so the user always knows where
          the photos will land. */}
      {state.attachments.length > 0 && (
        <label className="flex items-center gap-2 text-sm text-sharpenn-black cursor-pointer select-none pt-1">
          <input
            type="checkbox"
            checked={Boolean(state.saveToPortfolio)}
            onChange={(e) =>
              setState((prev) => ({
                ...prev,
                saveToPortfolio: e.target.checked,
              }))
            }
            className="w-4 h-4 rounded border-gray-300 text-sharpenn-coral focus:ring-sharpenn-coral cursor-pointer"
          />
          <span>
            Also save to{' '}
            {actorKind === 'business' && actor?.name
              ? `${actor.name}'s portfolio`
              : 'my portfolio'}
          </span>
        </label>
      )}

      {/* "Set as intro video" toggle — only when at least one attachment
          is a video. We auto-promote the FIRST video in the list (the
          natural primary). Posting as a business is excluded for now:
          the intro-video setter writes to the user's profile_medias and
          we don't yet have an equivalent UI surface for the business's
          intro reel. */}
      {state.attachments.some((a) => a.mediaType === 'video') &&
        actorKind !== 'business' && (
          <label className="flex items-center gap-2 text-sm text-sharpenn-black cursor-pointer select-none">
            <input
              type="checkbox"
              checked={Boolean(state.setAsIntroVideo)}
              onChange={(e) =>
                setState((prev) => ({
                  ...prev,
                  setAsIntroVideo: e.target.checked,
                }))
              }
              className="w-4 h-4 rounded border-gray-300 text-sharpenn-coral focus:ring-sharpenn-coral cursor-pointer"
            />
            <span>Also set as my branding reel</span>
          </label>
        )}

      {/* Media picker lives inside the body (rather than the shell footer)
          so it only appears for this post type. Other types can render
          their own type-specific affordances here. */}
      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={state.attachments.length >= MAX_MEDIA_COUNT}
          className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium text-sharpenn-coral hover:bg-sharpenn-blush/40 transition-colors disabled:opacity-50"
          aria-label="Add media"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
          Photo / video
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          className="hidden"
          onChange={(e) => {
            handleFilesSelected(e.target.files)
            e.target.value = ''
          }}
        />
        <span className="text-xs text-green-gray">
          {state.body.length} / {MAX_BODY_LENGTH}
        </span>
      </div>
    </div>
  )
}

const canSubmit = (state) => {
  const hasText = state.body.trim().length > 0
  const hasAttachments = state.attachments.length > 0
  return hasText || hasAttachments
}

const submit = async ({state, authorCtx, setStatus}) => {
  let mediaIds = []
  if (state.attachments.length > 0) {
    setStatus('Uploading...')
    // When the toggle is on we upload as `portfolio` so the same row
    // surfaces in the active identity's portfolio. Otherwise it's
    // `post_media`, which lives only inside the post.
    const uploadType = state.saveToPortfolio ? 'portfolio' : 'post_media'
    mediaIds = await Promise.all(
      state.attachments.map(async (a) => {
        const form = new FormData()
        form.append('file', a.file)
        form.append('type', uploadType)
        form.append('mediaType', a.mediaType)
        if (authorCtx.businessId) {
          form.append('businessId', authorCtx.businessId)
        }
        try {
          const result = await uploadFile(form)
          return result?.id
        } catch (err) {
          // Surface the backend's message (e.g. portfolio cap reached)
          // instead of the generic axios "Request failed with status…".
          const backendMsg = err?.response?.data?.message
          throw new Error(backendMsg || 'Upload failed')
        }
      })
    )
    mediaIds = mediaIds.filter(Boolean)
    if (mediaIds.length !== state.attachments.length) {
      throw new Error('One or more files failed to upload.')
    }
  }

  // If the user opted in, also upload the first attached video as their
  // intro / video resume. Done as a separate upload (not a "promote
  // existing media" call) because the only API today is uploadVideoResume
  // which expects a file. The cost is one extra round-trip and a small
  // amount of duplicated storage; the UX win — seamlessly capturing
  // intro videos from the composer — is worth it. Failures here don't
  // block the post: the post itself has already succeeded.
  if (state.setAsIntroVideo && !authorCtx.businessId) {
    const firstVideo = state.attachments.find((a) => a.mediaType === 'video')
    if (firstVideo?.file) {
      try {
        setStatus('Setting branding reel…')
        await uploadVideoResume(firstVideo.file)
      } catch (err) {
        // Surface but don't throw — the post already succeeded above.
        // eslint-disable-next-line no-console
        console.warn('Intro video set failed:', err?.message || err)
      }
    }
  }

  setStatus('Posting...')
  const trimmed = state.body.trim()
  // Return the payload; the shell calls POST /posts with it uniformly
  // across all post-type plugins (see PostComposer/index.js handleSubmit).
  return {
    type: mediaIds.length > 0 ? 'media' : 'text',
    body: trimmed || null,
    businessId: authorCtx.businessId,
    mediaIds,
  }
}

const postType = {
  id: 'post',
  label: 'Post',
  // A simple feather-style pen icon. Keeping icons inline as JSX so each
  // plugin file is self-contained.
  icon: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  // Every actor can make a plain post.
  canUse: () => true,
  // Plain posts can be authored as user or business; let the shell show
  // the acting-as toggle.
  requiresBusiness: false,
  createInitialState,
  Body: PostBody,
  canSubmit,
  submit,
  // The label shown on the primary action button when this type is active.
  submitLabel: 'Post',
}

export default postType
